import { useEffect, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Box,
  BoxProps,
  // eslint-disable-next-line no-restricted-imports
  Dialog as MUIDialog,
  DialogProps as MUIDialogProps,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';

import { OperationResult } from 'urql';

import CloseIcon from '@watershed/icons/components/Close';
import HelpIcon from '@watershed/icons/components/Help';
import mixinSx from '@watershed/style/mixinSx';

import Button, { ButtonProps } from '../Button';
import ProgressButton from '../ProgressButton';
import SheetPanel from './SheetPanel';
import SheetPanelHelp from './SheetPanelHelp';
import SheetTitle from './SheetTitle';

export type SheetProps = MUIDialogProps & {
  // We now require an id to drive E2E tests and logging
  // Note: This is not a DOM id, so it does not have to be unique on the page
  id: string;
  sidebar?: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  requestHelp?: (message: string) => Promise<OperationResult<any>>;
  closeButtonText?: LocalizedString;
  closeButtonTooltip?: LocalizedString;
  closeButtonVariant?: ButtonProps['variant'];
  closeButtonProps?: Partial<ButtonProps>;
  isCloseInProgress?: boolean;
  hideCloseButton?: boolean;
  topBarProps?: Partial<BoxProps>;
  onClose: () => void;
  fullScreen?: boolean;
  topBannerText?: LocalizedString | null;
  disableEscapeKeyDown?: boolean;

  helpButton?: {
    label: LocalizedString;
    tooltip?: LocalizedString;
    buttonProps: ButtonProps;
  };

  // Overrides for the help sheet that slides out from the right side.
  // By default (using `requestHelp` this component implements a simple
  // Help panel that lets the user type a message.
  // If sheetPanel is specified, the panel is used instead and no Help button
  // is included in the Sheet.
  // If sheetPanelOpenButton is specified, include that button in
  // the Sheet header.
  sheetPanel?: React.ReactNode;
  sheetPanelOpenButton?: React.ReactNode;
  footer?: React.ReactNode;
  title?: LocalizedString;
  sx?: SxProps<Theme>;

  dataTest?: string;
};

export default function Sheet({
  id,
  open,
  children,
  sidebar,
  onClose,
  requestHelp,
  hideCloseButton = false,
  closeButtonText,
  closeButtonTooltip = '',
  closeButtonProps = {},
  isCloseInProgress = false,
  helpButton,
  topBarProps = {},
  fullScreen,
  topBannerText,
  sheetPanel,
  sheetPanelOpenButton,
  disableEscapeKeyDown,
  closeButtonVariant = 'text',
  footer,
  title,
  sx,
  dataTest,
  ...muiDialogProps
}: SheetProps) {
  const { t } = useLingui();

  closeButtonText ??= t`Close`;

  const [panelOpen, setPanelOpen] = useState(false);
  useEffect(() => {
    if (open === false) {
      // Reset local state when sheet is closed, since it stays
      // mounted in the DOM.
      setPanelOpen(false);
    }
  }, [open]);

  return (
    <MUIDialog
      data-testid={dataTest ?? `Sheet-${id}`}
      disableEscapeKeyDown={disableEscapeKeyDown}
      sx={mixinSx(
        {
          pt: fullScreen ? 0 : 5,
          '& .MuiDialog-paperFullScreen': {
            borderRadius: fullScreen ? 0 : '20px 20px 0 0',
            background: 'white',
          },
        },
        sx
      )}
      open={open}
      onClose={onClose}
      fullScreen
      {...muiDialogProps}
    >
      {topBannerText && (
        <Box
          sx={{
            width: '100%',
            position: 'relative',
            zIndex: 1,
            py: 1,
            px: 2,
            backgroundColor: 'cobalt',
          }}
        >
          <Typography
            variant="h4"
            sx={{ color: (theme) => theme.palette.white }}
          >
            {topBannerText}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          height: topBannerText ? 'calc(100% - 35px)' : '100%',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxHeight: '100vh',
            height: '100%',
            display: {
              md: sidebar ? 'grid' : undefined,
            },
            gridTemplateColumns: {
              xs: sidebar ? `1fr` : undefined,
              md: sidebar ? `auto 1fr` : undefined,
            },
          }}
        >
          {sidebar}
          <Stack
            sx={{
              width: '100%',
              height: '100%',
              maxHeight: '100vh',
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <Box
              {...topBarProps}
              sx={mixinSx(
                {
                  position: { lg: 'sticky' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  zIndex: 1,
                  top: 0,
                  gap: 1,
                  right: 0,
                  paddingY: 2,
                  paddingX: 3,
                  marginBottom: -8 - 1 / 8, // to account for this total height of this element + its border!
                },
                topBarProps.sx
              )}
            >
              {title && (
                <SheetTitle sx={{ flex: 1, marginY: '8px' }} title={title} />
              )}
              {!sheetPanel && requestHelp && (
                <Button
                  data-testid="ButtonHelp"
                  variant="text"
                  onClick={() => setPanelOpen(true)}
                  disabled={panelOpen}
                  startIcon={<HelpIcon />}
                >
                  <Trans>Need help?</Trans>
                </Button>
              )}
              {sheetPanelOpenButton}

              {helpButton && (
                <Button
                  data-testid="ButtonLinkHelp"
                  endIcon={<HelpIcon />}
                  variant={closeButtonVariant}
                  {...helpButton.buttonProps}
                  tooltip={helpButton.tooltip}
                >
                  {helpButton.buttonProps.children ?? helpButton.label}
                </Button>
              )}

              <Tooltip title={closeButtonTooltip}>
                <ProgressButton
                  data-testid="ButtonClose"
                  variant={closeButtonVariant}
                  onClick={() => onClose()}
                  endIcon={<CloseIcon />}
                  {...closeButtonProps}
                  // We are doing a hack here to hide the close button using
                  // visibility instead of just not rendering it because the footer
                  // can get a little sad without the space that the close button takes up.
                  sx={hideCloseButton ? { visibility: 'hidden' } : {}}
                  isInProgress={isCloseInProgress}
                  color="secondary"
                >
                  {closeButtonProps.children ?? closeButtonText}
                </ProgressButton>
              </Tooltip>
            </Box>
            {children}
          </Stack>
        </Box>
        {sheetPanel ||
          (requestHelp && (
            <SheetPanel
              open={panelOpen}
              onClose={() => setPanelOpen(false)}
              title={<Trans>Need help?</Trans>}
            >
              <SheetPanelHelp
                onClose={() => setPanelOpen(false)}
                requestHelp={requestHelp}
              />
            </SheetPanel>
          ))}
      </Box>
      {footer && (
        <Box
          sx={{
            backgroundColor: (theme) => theme.palette.background.paper,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          {footer}
        </Box>
      )}
    </MUIDialog>
  );
}
